import { Helmet } from 'react-helmet';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { IconArrowLeft, IconChecks, IconEye, IconShoppingBag } from '@tabler/icons-react';
import { Breadcrumb, Button, Card, Carousel, Col, Empty, Flex, Form, Image, Input, Row, Select, Spin, Tooltip, notification } from 'antd';

import './TemplateDetail.css';
import router from '~/configs/routes';
import TemplateModun from './TemplateModun';
import { convertCurrency } from '~/configs';
import SlideImage from '~/components/SlideImage';
import imageNotFound from '~/assets/image/image_not.jpg';
import { logoutUserSuccess } from '~/redux/reducer/auth';
import { requestUserAddProductToCart } from '~/services/cart';
import { requestUserGetTemplateBySlug } from '~/services/template';
import IconCheckMarkDefault from '~/assets/icon/IconCheckMarkDefault';

const calculateTotalPrice = (services) => {
    if (!Array.isArray(services)) services = [services];

    const totalPrice = services.reduce((sum, service) => sum + (service?.price || 0), 0);

    const totalDiscountAmount = services.reduce((sum, service) => {
        return sum + ((service?.price || 0) * (service?.discount || 0)) / 100;
    }, 0);

    const finalPrice = totalPrice - totalDiscountAmount;

    return {
        price: totalPrice,
        payable: finalPrice,
        discount: totalDiscountAmount,
    };
};

function TemplateDetail() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [template, setTemplate] = useState(null);
    const [instanceId, setInstanceId] = useState(null);
    const [loadingPayment, setLoadingPayment] = useState(false);

    const [selectCycles, setSelectCycles] = useState(null);
    const [pricingProduct, setPricingProduct] = useState(null);
    const [pricingTemplate, setPricingTemplate] = useState(null);

    const { slug } = useParams();
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { currentUser } = useSelector((state) => state.auth);

    useEffect(() => {
        if (slug) {
            const fetch = async () => {
                setLoading(true);
                const result = await requestUserGetTemplateBySlug(slug);

                setLoading(false);
                if (result?.status === 200) {
                    setTemplate(result.data);
                    setProducts(result.data.cloud_server.products);

                    const pricing = result.data.pricing.at(-1);
                    if (pricing) {
                        setPricingTemplate(pricing);
                        setSelectCycles(pricing.cycles);
                    }
                } else {
                    navigate(router.templates);
                    notification.error({
                        message: 'Thông báo',
                        description: result?.error || 'Lỗi hệ thống vui lòng thử lại sau',
                    });
                }
            };
            fetch();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    const handleSelectCyclePayment = (pricingId) => {
        const pricing = template.pricing.find((pricing) => pricing.id === pricingId);
        if (pricing) {
            setPricingTemplate(pricing);
            setSelectCycles(pricing.cycles);

            const product = products.find((product) => product.id === instanceId);
            if (product) {
                const pricingProduct = product.pricings.find((pri) => pri.cycles.id === pricing.cycles.id);

                if (pricingProduct) {
                    setPricingProduct(pricingProduct);
                }
            }
        }
    };

    const handleViewDemoTemplate = (demo_url) => {
        if (!currentUser) {
            return notification.error({
                message: 'Thông báo',
                description: 'Vui lòng đăng nhập để tiếp tục',
            });
        }

        const url = demo_url || window.location.href;
        window.open(url, '_blank');
    };

    const handleSelectInstance = (id) => {
        const product = products.find((product) => product.id === id);
        if (product) {
            const pricing = product.pricings.find((pri) => pri.cycles.id === selectCycles.id);

            if (pricing) {
                setPricingProduct(pricing);
            }
        }

        setInstanceId(id);
        form.setFieldsValue({ instance_id: id });
    };

    const handlePaymentTemplate = async () => {
        try {
            const values = await form.validateFields();

            setLoadingPayment(true);

            const { domain, pricing_id, instance_id } = values;

            const data = {
                pricing_id,
                instance_id,
                id: template.id,
                domain: domain.toLowerCase(),
            };

            const result = await requestUserAddProductToCart('template', data);

            setLoadingPayment(false);
            if (result.status === 401 || result.status === 403) {
                dispatch(logoutUserSuccess());
                notification.error({
                    message: 'Thông báo',
                    description: 'Vui lòng đăng nhập để tiếp tục',
                });
            } else if (result?.status === 200) {
                navigate(router.cart);

                notification.success({
                    message: 'Thông báo',
                    description: result.message,
                });
            } else {
                notification.error({
                    message: 'Thông báo',
                    description: result?.error || 'Lỗi hệ thống vui lòng thử lại sau',
                });
            }
        } catch (error) {}
    };

    // Custom Prev Arrow
    const CustomPrevArrow = (props) => {
        const { className, style, onClick } = props;

        return (
            <div
                className={className}
                style={{
                    ...style,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    left: '-14px',
                    zIndex: 10,
                    width: 40,
                    height: 40,
                    background: '#fff',
                    borderRadius: '50%',
                    boxShadow: '0 0 5px rgba(0,0,0,0.3)',
                    cursor: 'pointer',
                }}
                onClick={onClick}
            >
                <LeftOutlined style={{ fontSize: 14 }} />
            </div>
        );
    };

    // Custom Next Arrow
    const CustomNextArrow = (props) => {
        const { className, style, onClick } = props;

        return (
            <div
                className={className}
                style={{
                    ...style,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    right: '-14px',
                    zIndex: 10,
                    width: 40,
                    height: 40,
                    background: '#fff',
                    borderRadius: '50%',
                    boxShadow: '0 0 5px rgba(0,0,0,0.3)',
                    cursor: 'pointer',
                }}
                onClick={onClick}
            >
                <RightOutlined style={{ fontSize: 14 }} />
            </div>
        );
    };

    const resultPrice = calculateTotalPrice([pricingTemplate, pricingProduct]);

    return (
        <Fragment>
            {template && (
                <Helmet>
                    <title>Thegioicode.com - {template.title}</title>
                    <meta key="description" name="description" content={`${template.description}`} />

                    <meta name="robots" content="index, follow" />
                    <meta property="og:image" content={`${template.image_url}`} />
                    <link rel="canonical" href={`https://thegioicode.com${pathname}`} />
                    <meta property="og:description" content={`${template.description}`} />
                    <meta property="og:url" content={`https://thegioicode.com${pathname}`} />
                    <meta property="og:title" content={`Thegioicode.com - ${template.title}`} />
                </Helmet>
            )}

            <Row style={{ rowGap: 16 }} className="template-wrapper">
                <Col span={24}>
                    <Flex className="gap-2 pl-2">
                        <Button size="small" className="box-center" onClick={() => navigate(router.templates)}>
                            <IconArrowLeft size={18} />
                        </Button>
                        <Breadcrumb
                            className="flex-1"
                            items={[
                                {
                                    title: <Link to={router.home}>Trang chủ</Link>,
                                },
                                {
                                    title: <Link to={router.templates}>Tạo website</Link>,
                                },
                                {
                                    title: template?.title,
                                },
                            ]}
                        />
                    </Flex>
                </Col>

                <Col md={19} xs={24} style={{ padding: '0 8px' }}>
                    <Card>
                        {!loading && template ? (
                            <Row style={{ margin: '0 -14px' }}>
                                <Col md={10} xs={24} style={{ padding: '0 14px' }}>
                                    <div className="mb-5">
                                        <h1 className="font-size-22 text-uppercase font-max mb-4">
                                            {template.title} {template.version && '-'}{' '}
                                            <span className="text-danger font-size-20">{template.version}</span>
                                        </h1>

                                        <div className="text-subtitle mb-xs-2">
                                            <p className="font-size-15" style={{ textAlign: 'justify' }}>
                                                {template.description}
                                            </p>
                                        </div>
                                    </div>

                                    <Image
                                        style={{ width: '100%', maxHeight: 460 }}
                                        src={template.image_url}
                                        alt={template.title}
                                        fallback={imageNotFound}
                                        className="border"
                                    />
                                </Col>

                                <Col md={14} xs={24} style={{ padding: '0 14px' }}>
                                    <Form
                                        layout="vertical"
                                        form={form}
                                        initialValues={{ pricing_id: template.pricing.at(-1).id }}
                                        scrollToFirstError
                                    >
                                        <Row gutter={16}>
                                            <Col md={12} xs={24}>
                                                <Form.Item
                                                    className="mb-3"
                                                    name="pricing_id"
                                                    label="Chu kỳ"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: 'Vui lòng chọn chu kỳ muốn tạo',
                                                        },
                                                    ]}
                                                >
                                                    <Select
                                                        size="large"
                                                        placeholder="Chọn chu kỳ"
                                                        onChange={handleSelectCyclePayment}
                                                        options={template.pricing.map((option) => {
                                                            return {
                                                                label: (
                                                                    <Fragment>
                                                                        <span>
                                                                            {convertCurrency(option.price * (1 - option.discount / 100))}
                                                                        </span>
                                                                        <span className="ml-1 text-subtitle font-size-12 text-line-through">
                                                                            {convertCurrency(option.price)}
                                                                        </span>
                                                                        <span> / </span>
                                                                        <span>{option.cycles.display_name}</span>
                                                                        <span className="ml-1 text-danger font-size-12">
                                                                            -{option.discount}%
                                                                        </span>
                                                                    </Fragment>
                                                                ),
                                                                value: option.id,
                                                            };
                                                        })}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col md={12} xs={24}>
                                                <Form.Item
                                                    name="domain"
                                                    label="Tên miền"
                                                    className="mb-0"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: 'Vui lòng nhập tên miền của bạn',
                                                        },
                                                        {
                                                            validator: async (_, value) => {
                                                                if (!value) return Promise.resolve();

                                                                if (/\.vn$/i.test(value)) {
                                                                    return Promise.reject('Không được dùng tên miền kết thúc bằng .vn');
                                                                }

                                                                const regex = /^(?!:\/\/)([a-zA-Z0-9-]{1,63})\.(?!vn$)[a-zA-Z]{2,6}$/;
                                                                if (!regex.test(value)) {
                                                                    return Promise.reject('Tên miền có định dạng như: thegioicode.com');
                                                                }

                                                                return Promise.resolve();
                                                            },
                                                        },
                                                    ]}
                                                >
                                                    <Input size="large" placeholder="VD: thegioicode.com" />
                                                </Form.Item>
                                            </Col>
                                            <Col md={24} xs={24}>
                                                <Form.Item
                                                    name="instance_id"
                                                    label="Máy chủ triển khai"
                                                    className="mb-0"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: 'Vui lòng chọn máy chủ triển khai',
                                                        },
                                                    ]}
                                                >
                                                    <Carousel
                                                        arrows
                                                        infinite={false}
                                                        slidesToShow={3}
                                                        slidesToScroll={1}
                                                        dots={false}
                                                        prevArrow={<CustomPrevArrow />}
                                                        nextArrow={<CustomNextArrow />}
                                                        responsive={[
                                                            {
                                                                breakpoint: 768,
                                                                settings: {
                                                                    slidesToShow: 2,
                                                                    slidesToScroll: 1,
                                                                    arrows: true,
                                                                },
                                                            },
                                                        ]}
                                                    >
                                                        {products.map((product) => (
                                                            <div key={product.id} className="px-2">
                                                                <div
                                                                    className="template-card"
                                                                    onClick={() => handleSelectInstance(product.id)}
                                                                    data-active={instanceId === product.id}
                                                                >
                                                                    <div className="text-center">
                                                                        <span className="font-weight-bold font-size-19 text-primary">
                                                                            {product.pricings
                                                                                .filter((pricing) => pricing.cycles.id === selectCycles.id)
                                                                                .map((pricing) =>
                                                                                    convertCurrency(
                                                                                        pricing.price * (1 - pricing.discount / 100),
                                                                                    ),
                                                                                )
                                                                                .shift() || 'N/A'}
                                                                        </span>
                                                                        <span className="text-subtitle font-size-13">
                                                                            {' '}
                                                                            / {selectCycles.value} Tháng
                                                                        </span>
                                                                    </div>
                                                                    {product.pricings
                                                                        .filter((pricing) => pricing.cycles.id === selectCycles.id)
                                                                        .map((pricing) => pricing.discount)
                                                                        .shift() !== 0 && (
                                                                        <div>
                                                                            <span className="font-size-15 text-center text-line-through text-subtitle">
                                                                                {product.pricings
                                                                                    .filter(
                                                                                        (pricing) => pricing.cycles.id === selectCycles.id,
                                                                                    )
                                                                                    .map((pricing) => convertCurrency(pricing.price))
                                                                                    .shift() || 'N/A'}
                                                                            </span>
                                                                            <span className="ml-1 text-danger font-size-12">
                                                                                -
                                                                                {product.pricings
                                                                                    .filter(
                                                                                        (pricing) => pricing.cycles.id === selectCycles.id,
                                                                                    )
                                                                                    .map((pricing) => pricing.discount)
                                                                                    .shift() || 'N/A'}
                                                                                %
                                                                            </span>
                                                                        </div>
                                                                    )}

                                                                    <div className="template-hr"></div>
                                                                    <div className="font-size-16 mb-1 text-center">
                                                                        <b>{product.title}</b>
                                                                    </div>
                                                                    <div className="font-size-16 mb-1 text-center">
                                                                        <b>{product.core}</b> vCPU
                                                                    </div>
                                                                    <div className="font-size-16 mb-1 text-center">
                                                                        <b>{product.memory / 1024}</b> GB RAM
                                                                    </div>
                                                                    <div className="font-size-16 mb-1 text-center">
                                                                        <b>{product.disk}</b> GB SSD
                                                                    </div>

                                                                    {instanceId === product.id && <IconCheckMarkDefault />}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </Carousel>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Form>

                                    <div className="mt-4 pl-2">
                                        Nếu muốn cài đặt máy chủ riêng vui lòng xem <Link to={router.cloud_server}>tại đây</Link>
                                    </div>
                                </Col>

                                <Col span={24} className="pt-5">
                                    <div className="title-custom font-size-16 text-uppercase">Ảnh demo</div>

                                    <SlideImage images={template.image_meta} />
                                </Col>

                                <Col span={24} className="pt-5">
                                    <div className="title-custom font-size-16 text-uppercase">Chi tiết phần mềm</div>

                                    <TemplateModun
                                        details={template.modules.map((module, index) => {
                                            return {
                                                key: Math.random(),
                                                index: index + 1,
                                                ...module,
                                            };
                                        })}
                                    />
                                </Col>

                                <Col span={24} className="pt-5">
                                    <div className="title-custom font-size-16 text-uppercase">Đánh giá</div>

                                    <Empty description="Chưa có đánh giá nào" />
                                </Col>

                                <Col span={24} className="pt-5">
                                    <div className="title-custom font-size-16 text-uppercase">Lịch xử cập nhật</div>

                                    <Empty description="Chưa có bản cập nhật nào" />
                                </Col>
                            </Row>
                        ) : (
                            <Flex align="center" justify="center" style={{ minHeight: '68vh' }}>
                                <Spin />
                            </Flex>
                        )}
                    </Card>
                </Col>

                <Col md={5} xs={24} style={{ padding: '0 8px' }}>
                    <Card
                        styles={{ body: { padding: 0 } }}
                        title={
                            <div className="d-flex align-items-center">
                                <IconShoppingBag size={22} />
                                <h2 className="font-size-17 mb-0 font-bold ml-2">Thông tin hóa đơn</h2>
                            </div>
                        }
                    >
                        <div className="border-top">
                            <Fragment>
                                <Flex align="center" justify="space-between" className="px-3 py-2 border-bottom">
                                    <div className="font-bold">Thành tiền</div>
                                    <span className="font-bold">{convertCurrency(resultPrice.price)}</span>
                                </Flex>
                                <Flex align="center" justify="space-between" className="px-3 py-2 border-bottom">
                                    <div className="font-bold">Chiết khấu</div>
                                    <span className="font-bold text-danger">{convertCurrency(-resultPrice.discount)}</span>
                                </Flex>
                            </Fragment>

                            <Flex align="center" justify="space-between" className="px-3 py-2 border-bottom">
                                <div className="font-bold">Tổng tiền</div>
                                <span className="font-size-18 font-bold text-primary">{convertCurrency(resultPrice.payable)}</span>
                            </Flex>

                            <div className="px-3 py-2 d-flex gap-2">
                                <Tooltip title="Xem mẫu">
                                    <Button
                                        size="large"
                                        type="primary"
                                        className="bg-warning box-center"
                                        disabled={loadingPayment}
                                        onClick={() => handleViewDemoTemplate(template.demo_url)}
                                        icon={<IconEye size={24} />}
                                    ></Button>
                                </Tooltip>
                                <Button
                                    size="large"
                                    type="primary"
                                    className="box-center flex-1"
                                    icon={<IconChecks size={24} />}
                                    onClick={handlePaymentTemplate}
                                    loading={loadingPayment}
                                >
                                    {loadingPayment ? <span className="ml-1">Đang xử lý...</span> : <span>Tiếp tục</span>}
                                </Button>
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>
        </Fragment>
    );
}

export default TemplateDetail;
